import { buildTrainingLocation } from '@/pages/centrumSzkolen/Szkolenia/common/buildTrainingLocation'

const escapeIcsText = value => {
	if (!value) return ''

	return `${value}`
		.replace(/\\/g, '\\\\')
		.replace(/;/g, '\\;')
		.replace(/,/g, '\\,')
		.replace(/\r\n/g, '\\n')
		.replace(/\n/g, '\\n')
}

const formatIcsDateTime = (dateValue, timeValue) => {
	const date = `${dateValue || ''}`.substring(0, 10)
	const time = `${timeValue || ''}`.trim()

	if (!/^\d{4}-\d{2}-\d{2}$/.test(date) || !/^\d{1,2}:\d{2}$/.test(time)) {
		return null
	}

	const [year, month, day] = date.split('-')
	const [hours, minutes] = time.split(':')

	return `${year}${month}${day}T${hours.padStart(2, '0')}${minutes.padStart(2, '0')}00`
}

const formatIcsDay = dateValue => {
	const date = `${dateValue || ''}`.substring(0, 10)
	if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) return null
	return date.replace(/-/g, '')
}

const addIcsDays = (compactDay, days) => {
	const year = Number(compactDay.slice(0, 4))
	const month = Number(compactDay.slice(4, 6))
	const day = Number(compactDay.slice(6, 8))
	const date = new Date(Date.UTC(year, month - 1, day))
	date.setUTCDate(date.getUTCDate() + days)
	const nextYear = date.getUTCFullYear()
	const nextMonth = `${date.getUTCMonth() + 1}`.padStart(2, '0')
	const nextDay = `${date.getUTCDate()}`.padStart(2, '0')
	return `${nextYear}${nextMonth}${nextDay}`
}

const formatIcsUtcTimestamp = (date = new Date()) => (
	date.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '')
)

const foldIcsLine = line => {
	if (line.length <= 75) return line

	const parts = [line.slice(0, 75)]
	let remaining = line.slice(75)

	while (remaining.length > 0) {
		parts.push(` ${remaining.slice(0, 74)}`)
		remaining = remaining.slice(74)
	}

	return parts.join('\r\n')
}

const foldIcsContent = content => (
	content
		.split('\r\n')
		.map(foldIcsLine)
		.join('\r\n')
)

const resolveTrainingLocation = training => {
	const builtLocation = buildTrainingLocation({
		miasto: training?.miasto,
		ulica: training?.ulica,
		kod_pocztowy: training?.kod_pocztowy,
		location: training?.location,
	})

	return `${builtLocation || training?.location || ''}`.trim()
}

const resolveTrainingRemarks = training => `${training?.remarks || training?.uwagi || ''}`.trim()

const GOOGLE_CALENDAR_TIMEZONE = 'Europe/Warsaw'

const resolveTrainingSchedule = training => {
	const allDayStart = training?.allDay ? formatIcsDay(training?.data_rozpoczecia) : null
	const allDayEnd = allDayStart
		? addIcsDays(formatIcsDay(training?.data_zakonczenia) || allDayStart, 1)
		: null
	const startDateTime = allDayStart ? null : formatIcsDateTime(
		training?.data_rozpoczecia,
		training?.startTime || training?.godzina_rozpoczecia
	)
	const endDateTime = allDayStart ? null : formatIcsDateTime(
		training?.data_zakonczenia || training?.data_rozpoczecia,
		training?.endTime || training?.godzina_zakonczenia
	)

	return { allDayStart, allDayEnd, startDateTime, endDateTime }
}

const buildGoogleCalendarDetails = remarks => {
	const text = `${remarks || ''}`.trim()
	if (!text) return ''

	const url = text.match(/https?:\/\/[^\s]+/)?.[0] || ''
	const rest = url
		? text
			.replace(url, '')
			.replace(/Link do projektu:\s*/i, '')
			.replace(/\n{3,}/g, '\n\n')
			.trim()
		: text
	return [url, rest].filter(Boolean).join('\n\n')
}

export const canBuildTrainingIcs = training => {
	if (!`${training?.nazwa || ''}`.trim()) return false

	if (training?.allDay) {
		return Boolean(formatIcsDay(training?.data_rozpoczecia))
	}

	const startDateTime = formatIcsDateTime(
		training?.data_rozpoczecia,
		training?.startTime || training?.godzina_rozpoczecia
	)
	const endDateTime = formatIcsDateTime(
		training?.data_zakonczenia || training?.data_rozpoczecia,
		training?.endTime || training?.godzina_zakonczenia
	)

	return Boolean(startDateTime && endDateTime)
}

export const buildTrainingIcsFilename = training => {
	const name = `${training?.nazwa || 'szkolenie'}`
		.trim()
		.normalize('NFD')
		.replace(/[\u0300-\u036f]/g, '')
		.replace(/[^\w\s-]/g, '')
		.replace(/\s+/g, '-')
		.replace(/-+/g, '-')
		.replace(/^-|-$/g, '')
		.slice(0, 80) || 'szkolenie'

	return `${name}.ics`
}

export const buildTrainingIcs = training => {
	const summary = escapeIcsText(`${training?.nazwa || ''}`.trim())
	const location = escapeIcsText(resolveTrainingLocation(training))
	const description = escapeIcsText(resolveTrainingRemarks(training))
	const uid = `${training?._id || Date.now()}@centrum-szkolen`
	const dtStamp = formatIcsUtcTimestamp()
	const { allDayStart, allDayEnd, startDateTime, endDateTime } = resolveTrainingSchedule(training)

	const lines = [
		'BEGIN:VCALENDAR',
		'VERSION:2.0',
		'PRODID:-//Centrum Szkolen//Training Calendar//PL',
		'CALSCALE:GREGORIAN',
		'METHOD:PUBLISH',
		'BEGIN:VEVENT',
		`UID:${uid}`,
		`DTSTAMP:${dtStamp}`,
		allDayStart ? `DTSTART;VALUE=DATE:${allDayStart}` : `DTSTART:${startDateTime}`,
		allDayStart ? `DTEND;VALUE=DATE:${allDayEnd}` : `DTEND:${endDateTime}`,
		`SUMMARY:${summary}`,
	]

	if (location) {
		lines.push(`LOCATION:${location}`)
	}

	if (description) {
		lines.push(`DESCRIPTION:${description}`)
	}

	lines.push('END:VEVENT', 'END:VCALENDAR')

	return foldIcsContent(`${lines.join('\r\n')}\r\n`)
}

export const buildGoogleCalendarEventDescription = training => (
	buildGoogleCalendarDetails(resolveTrainingRemarks(training))
)

export const buildGoogleCalendarUrl = training => {
	if (!canBuildTrainingIcs(training)) return ''

	const { allDayStart, allDayEnd, startDateTime, endDateTime } = resolveTrainingSchedule(training)
	const params = new URLSearchParams({
		action: 'TEMPLATE',
		text: `${training?.nazwa || ''}`.trim(),
		dates: allDayStart ? `${allDayStart}/${allDayEnd}` : `${startDateTime}/${endDateTime}`,
		ctz: GOOGLE_CALENDAR_TIMEZONE,
	})
	const details = buildGoogleCalendarEventDescription(training)
	const location = resolveTrainingLocation(training)

	if (details) params.set('details', details)
	if (location) params.set('location', location)

	return `https://calendar.google.com/calendar/render?${params.toString()}`
}
